import { useId } from 'react';

import { Label } from 'cpool-components/general/Label';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { formatFormLabel } from '../formatFormLabel';

type FormDropdownProps<TFieldValues extends FieldValues> = {
    control: Control<TFieldValues>;
    disabled?: boolean;
    id: Path<TFieldValues>;
    label: string;
    labelClassName?: string;
    options: { label: string; value: string }[];
    placeholder?: string;
    required?: boolean;
    searchable?: boolean;
};

export const FormDropdown = <TFieldValues extends FieldValues>({
    control,
    disabled,
    id,
    label,
    labelClassName = 'h-8 w-[175px] shrink-0 justify-start',
    options,
    placeholder = '',
    required,
    searchable = true,
}: FormDropdownProps<TFieldValues>) => {
    const labelId = useId();

    return (
        <Controller
            name={id}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <div className="flex items-start gap-2.5">
                    <Label className={labelClassName} id={labelId}>
                        {formatFormLabel(label, required)}
                    </Label>
                    <DropdownMenu
                        aria-labelledby={labelId}
                        data-testid={id}
                        options={options}
                        value={field.value as string}
                        onChange={(value) => field.onChange(value)}
                        placeholder={placeholder}
                        triggerClassName="w-full justify-between"
                        className="w-full"
                        disabled={disabled}
                        error={error?.message}
                        searchable={searchable}
                        scrollable
                    />
                </div>
            )}
        />
    );
};
